/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	createEventEmitter,
	emitEvent,
	onEvent,
	TFrozenType,
	TSimpleEventEmitter
} from "@adapptio/lib-shared";
import {
	FloatingPanelID,
	FloatingPanelModel,
	IFloatingPanelModelConstructor,
	IFloatingPanelParams,
	IFloatingPanelRect,
	TGetFloatingPanelOpenParams
} from "./FloatingPanelModel";
import { Breadcrumb } from "../../../decorators";

/**
 * FloatingPanel entry states
 */
export enum FLOATING_PANEL_ITEM_STATE {
	INIT = "init",
	OPENED = "open",
	CLOSED = "closed"
}

/**
 * Floating panel entry
 */
export interface IFloatingPanelEntry {
	readonly id: FloatingPanelID;
	readonly instance: FloatingPanelModel;
	readonly onStateChange: TSimpleEventEmitter<void>;
	state: FLOATING_PANEL_ITEM_STATE;
}

type TFloatingPanelInstanceIndex = Record<string, IFloatingPanelEntry>;

const FLOATING_PANEL_OPEN_DELAY = 10;
const FLOATING_PANEL_CLOSE_DELAY = 600;

/**
 * Service options
 */
export interface IFloatingPanelManagerOpts {}

/**
 * Manager of floating panels
 */
export class FloatingPanelManager {
	/** Opened floating panel items */
	private panels: IFloatingPanelEntry[] = [];

	/** ID index */
	private idIndex: TFloatingPanelInstanceIndex = {};

	/** Emitted when entries list has changed */
	public readonly onEntriesChange = createEventEmitter<void>();

	/** Emitted when a floating panel was opened */
	public readonly onFloatingPanelOpen = createEventEmitter<FloatingPanelModel>();

	/** Emitted when a floating panel has been closed */
	public readonly onFloatingPanelClose = createEventEmitter<FloatingPanelModel>();

	/** Panel rect storage (positions and sizes) */
	private rects = new Map<string, IFloatingPanelRect>();

	/**
	 * Opens floating panel and returns promise that is resolved when the floating panel is closed
	 *
	 * @param ctor FloatingPanel constructor
	 * @param id Unique floating panel ID
	 * @param params Parameters
	 */
	@Breadcrumb("panel.open", (ctor, id) => String(id), (a, b, params) => ({ params }))
	public open<
		TFloatingPanelModel extends FloatingPanelModel,
		Params extends IFloatingPanelParams = TGetFloatingPanelOpenParams<TFloatingPanelModel>
	>(
		ctor: IFloatingPanelModelConstructor<TFloatingPanelModel, Params>,
		id: FloatingPanelID,
		params: Params
	): Promise<void> {
		// Instance with ID that already exists
		if (this.idIndex[id]) {
			if (this.idIndex[id].instance instanceof ctor) {
				return this.idIndex[id].instance.getClosePromise() as Promise<void>;
			} else {
				throw new Error(
					`FloatingPanel with ID '${id}' is already opened and has different constructor.`
				);
			}
		}

		const initParams: Params = {
			...params
		};

		if (this.hasStoredRect(id)) {
			initParams.defaultRect = this.getStoredRect(id);
		}

		// Create new panel entry
		const entry: IFloatingPanelEntry = {
			id: id,
			instance: new ctor(id, initParams),
			state: FLOATING_PANEL_ITEM_STATE.INIT,
			onStateChange: createEventEmitter()
		};

		this.addNewEntry(entry);

		return entry.instance.getClosePromise() as Promise<void>;
	}

	private addNewEntry(entry: IFloatingPanelEntry): void {
		this.storeEntry(entry);

		this.setEntryOpenTimer(entry);
		this.watchEntryRect(entry);

		emitEvent(this.onEntriesChange);
	}

	private storeEntry(entry: IFloatingPanelEntry): void {
		this.panels.push(entry);
		this.idIndex[entry.id] = entry;
	}

	private setEntryOpenTimer(entry: IFloatingPanelEntry): void {
		const openTimer = setTimeout(() => {
			entry.state = FLOATING_PANEL_ITEM_STATE.OPENED;
			emitEvent(entry.onStateChange);

			try {
				emitEvent(this.onFloatingPanelOpen, entry.instance);
			} catch (err) {
				console.warn(
					`Failed to handle onFloatingPanelOpen event for floating panel with ID '${entry.id}':`,
					err
				);
			}
		}, FLOATING_PANEL_OPEN_DELAY);

		entry.instance.getClosePromise().then(() => {
			clearTimeout(openTimer);

			entry.state = FLOATING_PANEL_ITEM_STATE.CLOSED;
			emitEvent(entry.onStateChange);

			try {
				emitEvent(this.onFloatingPanelClose, entry.instance);
			} catch (err) {
				console.warn(
					`Failed to handle onFloatingPanelClose event for floating panel with ID '${entry.id}':`,
					err
				);
			}

			setTimeout(() => {
				this.destroyFloatingPanel(entry);
			}, FLOATING_PANEL_CLOSE_DELAY);
		});
	}

	private hasStoredRect(id: FloatingPanelID): boolean {
		return this.rects.has(id);
	}

	private getStoredRect(id: FloatingPanelID): IFloatingPanelRect {
		return this.rects.get(id);
	}

	private setStoredRect(id: FloatingPanelID, position: IFloatingPanelRect): void {
		this.rects.set(id, position);
	}

	private refreshEntryStoredRect(entry: IFloatingPanelEntry): void {
		this.setStoredRect(entry.id, entry.instance.getRect());
	}

	private watchEntryRect(entry: IFloatingPanelEntry): void {
		onEvent(entry.instance.onRectChange, () => {
			this.refreshEntryStoredRect(entry);
		});
	}

	/**
	 * Returns current floating panel entries
	 */
	public getEntries(): TFrozenType<IFloatingPanelEntry[]> {
		return [ ...this.panels ];
	}

	/**
	 * Returns floating panel instance by ID or null if not exists
	 *
	 * @param id FloatingPanel ID
	 */
	public getFloatingPanelById<TFloatingPanelModel extends FloatingPanelModel>(
		id: FloatingPanelID
	): TFloatingPanelModel {
		return (this.idIndex[id].instance as TFloatingPanelModel) || null;
	}

	/**
	 * Returns if a floating panel with given ID exists
	 *
	 * @param id FloatingPanel ID
	 */
	public isOpened(id: FloatingPanelID): boolean {
		return !!this.idIndex[id];
	}

	/**
	 * Disposes floating panel instance and removes it from list and index
	 *
	 * @param entry FloatingPanel entry
	 */
	@Breadcrumb("panel.destroy", (entry: IFloatingPanelEntry) => entry?.id)
	private destroyFloatingPanel(entry: IFloatingPanelEntry) {
		const i = this.panels.indexOf(entry);

		if (i >= 0) {
			this.panels.splice(i, 1);
		}

		delete this.idIndex[entry.id];

		try {
			entry.instance.dispose();
		} catch (err) {
			console.warn(`Failed to dispose floating panel ID '${entry.id}':`, err);
		}

		emitEvent(this.onEntriesChange);
	}

	public dispose(): void {
		this.getEntries().forEach((entry) => this.destroyFloatingPanel(entry));
	}
}
